/**
 * Formats a timestamp as relative time (e.g., "2 hours ago", "in 3 days")
 * Handles both past and future dates
 */
export const formatRelativeTime = (timestamp: string | number | Date | null | undefined): string => {
  if (!timestamp) {
    return 'Never';
  }

  const nowSeconds = Math.floor(Date.now() / 1000);
  let referenceTime: number;

  if (typeof timestamp === 'string') {
    referenceTime = Math.floor(new Date(timestamp).getTime() / 1000);
  } else if (timestamp instanceof Date) {
    referenceTime = Math.floor(timestamp.getTime() / 1000);
  } else {
    referenceTime = Math.floor(Number(timestamp));
  }

  if (isNaN(referenceTime)) {
    return 'Invalid date';
  }

  const difference = referenceTime - nowSeconds;
  const absDifference = Math.abs(difference);

  // Future dates
  if (difference > 0) {
    if (absDifference < 60) {
      return `in ${absDifference} ${absDifference === 1 ? 'second' : 'seconds'}`;
    } else if (absDifference < 3600) {
      const mins = Math.floor(absDifference / 60);
      return `in ${mins} ${mins === 1 ? 'minute' : 'minutes'}`;
    } else if (absDifference < 86400) {
      const hours = Math.floor(absDifference / 3600);
      return `in ${hours} ${hours === 1 ? 'hour' : 'hours'}`;
    } else if (absDifference < 2620800) {
      const days = Math.floor(absDifference / 86400);
      return `in ${days} ${days === 1 ? 'day' : 'days'}`;
    } else if (absDifference < 31449600) {
      const months = Math.floor(absDifference / 2620800);
      return `in ${months} ${months === 1 ? 'month' : 'months'}`;
    } else {
      const years = Math.floor(absDifference / 31449600);
      return `in ${years} ${years === 1 ? 'year' : 'years'}`;
    }
  }
  // Past dates
  else {
    if (absDifference < 60) {
      return `${absDifference} ${absDifference === 1 ? 'second' : 'seconds'} ago`;
    } else if (absDifference < 3600) {
      const mins = Math.floor(absDifference / 60);
      return `${mins} ${mins === 1 ? 'minute' : 'minutes'} ago`;
    } else if (absDifference < 86400) {
      const hours = Math.floor(absDifference / 3600);
      return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
    } else if (absDifference < 2620800) {
      const days = Math.floor(absDifference / 86400);
      return `${days} ${days === 1 ? 'day' : 'days'} ago`;
    } else if (absDifference < 31449600) {
      const months = Math.floor(absDifference / 2620800);
      return `${months} ${months === 1 ? 'month' : 'months'} ago`;
    } else {
      const years = Math.floor(absDifference / 31449600);
      return `${years} ${years === 1 ? 'year' : 'years'} ago`;
    }
  }
};

/**
 * Checks if a timestamp has expired (is in the past)
 */
export const isExpired = (expiresAt: string | number | Date | null | undefined): boolean => {
  if (!expiresAt) {
    return false;
  }

  const now = Date.now();
  let expiryTime: number;

  if (typeof expiresAt === 'string') {
    expiryTime = new Date(expiresAt).getTime();
  } else if (expiresAt instanceof Date) {
    expiryTime = expiresAt.getTime();
  } else {
    expiryTime = Number(expiresAt) * 1000; // Assume Unix timestamp in seconds
  }

  if (isNaN(expiryTime)) {
    return false;
  }

  return expiryTime < now;
};
